// src/features/tasks/components/TaskDetailsPage/EditEquipmentDialog.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { RELATION_TYPES } from '../../../equipment/utils/enums';
import RotationOptionsEditor from './RotationOptionsEditor';

export default function EditEquipmentDialog({
  open,
  onClose,
  onSubmit,
  link,
  submitLoading = false,
  submitError = null,
}) {
  const { t } = useTranslation(['tasks', 'equipment', 'common']);

  const [relationType, setRelationType] = useState('KONSTANS');
  const [notes, setNotes] = useState('');
  const [rotationEnabled, setRotationEnabled] = useState(false);
  const [rotationLabel, setRotationLabel] = useState('');
  const [rotationOptions, setRotationOptions] = useState([]);

  useEffect(() => {
    if (open && link) {
      setRelationType(link.relation_type || 'KONSTANS');
      setNotes(link.notes || '');
      setRotationEnabled(Boolean(link.rotation_options?.length));
      setRotationLabel(link.rotation_label || '');
      setRotationOptions(link.rotation_options || []);
    }
  }, [open, link]);

  const handleSubmit = () => {
    onSubmit({
      relation_type: relationType,
      notes: notes || null,
      rotation_label: rotationEnabled ? (rotationLabel.trim() || null) : null,
      rotation_options: rotationEnabled && rotationOptions.length > 0 ? rotationOptions : null,
    });
  };

  if (!link) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('tasks:equipment.edit_link_title', { defaultValue: 'Edit Equipment Link' })}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {submitError && <Alert severity="error">{submitError}</Alert>}

          <Typography variant="body2" color="text.secondary">
            {link.equipment?.name}
          </Typography>

          <TextField
            select
            label={t('tasks:equipment.relation_type', { defaultValue: 'Relation Type' })}
            value={relationType}
            onChange={(e) => setRelationType(e.target.value)}
            required
            disabled={submitLoading}
          >
            {RELATION_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {t(`equipment:relation_types.${type.value}`)}
              </MenuItem>
            ))}
          </TextField>

          <RotationOptionsEditor
            enabled={rotationEnabled}
            onEnabledChange={setRotationEnabled}
            rotationLabel={rotationLabel}
            onRotationLabelChange={setRotationLabel}
            rotationOptions={rotationOptions}
            onRotationOptionsChange={setRotationOptions}
            disabled={submitLoading}
          />

          <TextField
            label={t('common:labels.notes')}
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            inputProps={{ maxLength: 500 }}
            helperText={`${notes.length}/500 ${t('common:labels.characters', { defaultValue: 'characters' })}`}
            disabled={submitLoading}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitLoading}>
          {t('common:buttons.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitLoading}
          startIcon={submitLoading && <CircularProgress size={16} />}
        >
          {submitLoading ? t('common:buttons.saving') : t('common:buttons.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
